import React, { useEffect, useState } from 'react';
import { useAppState } from '../../AppContext';
import { cumsum } from 'd3-array';

import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import ClusterScatterplot from '../scatterplots/ClusterScatterplot';

import { getAvailableHeight, getAvailableWidth } from '../utils';
import ClusterScatterplotMinimap from '../scatterplots/ClusterScatterplotMinimap';

import CollapsedGroup from './CollapsedGroup';
import DocumentTitles from './DocumentTitles';
import ExpandedGroup from './ExpandedGroup';
import KeywordLabels from './KeywordLabels';
import Control from './Control';

import {
  textHeight,
  keywordWidth,
  padding,
  metadataColumnWidth
} from './constants';
import GroupTitle from './GroupTitle';
import MetadataColumnLabels from './MetadataColumnLabels';

const DocumentTermMatrix = ({ highlightSnippet }) => {
  const [selectedColumn, setSelectedColumn] = useState();

  const [
    {
      filterKeywords,
      matrixVisibleKeywords,
      visibleMetadataColumns,
      documentCluster,
      tdMatrix,
      keywordsInCluster,
      clusterData,
      clustersInTable: clusters
    },
    dispatch
  ] = useAppState();

  useEffect(() => {
    // Only load tdMatrix once
    if (clusterData && !tdMatrix) {
      fetch('/nlp/documents_keywords')
        .then((res) => res.json())
        .then((data) => {
          // mapping from keyword -> documents
          dispatch({ type: 'SET_TD_MATRIX', tdMatrix: data });
        });
    }
  }, [documentCluster]);

  if (!clusters || clusters.length === 0) {
    return null;
  }

  /* filter clusters to remove any documents that omit a required keyword */

  /* *** */

  const paddingBetweenClusters = 10;

  const height = clusters.map((c) =>
    c.expanded ? keywordWidth * c.members.length : keywordWidth
  );
  const expanded = [0, ...cumsum(clusters.map((c) => (c.expanded ? 1 : 0)))];

  const startYPos = [0, ...cumsum(height)].map(
    (val, i) => val + i * paddingBetweenClusters + expanded[i] * keywordWidth
  );
  const matrixHeight = startYPos[startYPos.length - 1];

  const numColsBetweenClusters = 0.5;

  let completeStartCols = [];
  completeStartCols[clusters.length - 1] = 0;

  let nextStep = clusters[clusters.length - 1].expanded
    ? clusters[clusters.length - 1].keywords.length
    : -numColsBetweenClusters;

  for (let j = clusters.length - 2; j >= 0; j--) {
    if (clusters[j].expanded) {
      completeStartCols[j] =
        completeStartCols[j + 1] + nextStep + numColsBetweenClusters;
      nextStep = clusters[j].keywords.length;
    } else {
      completeStartCols[j] = completeStartCols[j + 1];
    }
  }

  const totalWidth =
    keywordWidth * matrixVisibleKeywords.length +
    metadataColumnWidth * visibleMetadataColumns.length +
    2 * padding +
    50;

  const rightColumnWidth = Math.min(1400, getAvailableWidth() / 2);

  const maxClusterSize = Math.max(...clusters.map((c) => c.members.length));

  const svgRef = React.createRef();

  const scatterplotHeight = Math.min(
    500,
    Math.min(getAvailableWidth() / 3, getAvailableHeight() / 3)
  );

  return (
    <div
      style={{
        width: totalWidth + rightColumnWidth,
        marginLeft: 'auto',
        height: getAvailableHeight() - 100,
        overflowY: 'auto',
        scrollBehavior: 'smooth'
      }}
    >
      <br />
      <Control />
      <br />

      {filterKeywords.length > 0 && (
        <p>
          {' '}
          <Button
            variant="outline-secondary"
            onClick={() => dispatch({ type: 'CLEAR_KEYWORD_FILTERS' })}
          >
            Clear all keyword filters
          </Button>
        </p>
      )}

      {/* SVG For header row */}
      <div
        style={{
          position: 'sticky',
          zIndex: 99,
          top: 40,
          left: '0px',
          width: totalWidth + rightColumnWidth,
          /* N.B. this is the sie of the main SVG, which may be larger than the screen width (which would be 100%) */
          background: 'white',
          /* overflowY: 'scroll', */
          whiteSpace: 'nowrap'
        }}
      >
        <svg
          width={totalWidth}
          height={Math.max(scatterplotHeight, textHeight + 100)}
        >
          <g transform={`translate(${0}, 0)`}>
            <rect
              x={0}
              y={0}
              width={totalWidth}
              height={textHeight + 100}
              stroke="none"
              fill="white"
            />
            <g
              transform={`translate(${padding},${
                padding + scatterplotHeight - (textHeight + 100)
              })`}
            >
              <KeywordLabels
                keywords={matrixVisibleKeywords}
                tdMatrix={tdMatrix}
                selectedColumn={selectedColumn}
              />
            </g>

            <g
              transform={`translate(${
                2 * padding + matrixVisibleKeywords.length * keywordWidth
              },${padding + scatterplotHeight - (textHeight + 100)})`}
            >
              <MetadataColumnLabels
                metadataColumns={visibleMetadataColumns}
                tdMatrix={tdMatrix}
                selectedColumn={selectedColumn}
              />
            </g>
          </g>
        </svg>

        {clusters && (
          <div
            style={{
              position: 'sticky',
              right: 0,
              display: 'inline-block',
              background: 'white',
              width: `${rightColumnWidth}px`,
              float: 'right'
            }}
          >
            <ClusterScatterplot
              highlightSnippet={highlightSnippet}
              scatterplotSize={scatterplotHeight}
              svgStyle={{
                width: scatterplotHeight
              }}
            />

            <ClusterScatterplotMinimap
              highlightSnippet={highlightSnippet}
              scatterplotSize={scatterplotHeight}
              svgStyle={{
                width: Math.round(scatterplotHeight / 2),
                marginLeft: '10px'
              }}
            />
          </div>
        )}
      </div>

      <div
        style={{
          whiteSpace: 'nowrap',
          width: `${totalWidth + rightColumnWidth}px`
        }}
      >
        {/* SVG for main matrix */}
        <svg
          width={totalWidth}
          height={matrixHeight + padding}
          ref={svgRef}
          style={{ float: 'left' }}
        >
          <g transform={`translate(${padding}, 0)`}>
            {/* Cluster rectangles */}
            {clusters.map((c, i) =>
              c.expanded ? (
                <ExpandedGroup
                  key={i}
                  cluster={c}
                  startY={startYPos[i]}
                  toggleCollapsed={() =>
                    dispatch({ type: 'TOGGLE_CLUSTER_COLLAPSED', cluster: c })
                  }
                  tdMatrix={tdMatrix}
                  matrixWidth={totalWidth}
                  keywords={matrixVisibleKeywords}
                  selectedColumn={selectedColumn}
                  setSelectedColumn={setSelectedColumn}
                />
              ) : (
                <CollapsedGroup
                  key={i}
                  cluster={c}
                  startY={startYPos[i]}
                  toggleCollapsed={() =>
                    dispatch({ type: 'TOGGLE_CLUSTER_COLLAPSED', cluster: c })
                  }
                  keywords={matrixVisibleKeywords}
                  selectedColumn={selectedColumn}
                  setSelectedColumn={setSelectedColumn}
                  keywordsInCluster={keywordsInCluster}
                />
              )
            )}
          </g>
        </svg>

        {/* SVG for titles to the right */}

        <svg
          width={rightColumnWidth}
          height={matrixHeight + padding}
          style={{
            position: 'sticky',
            right: 0,
            background: 'white',
            float: 'right'
          }}
        >
          {clusters.map((c, i) => (
            <React.Fragment key={i}>
              <GroupTitle
                startY={startYPos[i]}
                toggleCollapsed={() =>
                  dispatch({ type: 'TOGGLE_CLUSTER_COLLAPSED', cluster: c })
                }
                cluster={c}
                maxClusterSize={maxClusterSize}
              />

              {/* Document titles */}
              {c.expanded && (
                <DocumentTitles cluster={c} startY={startYPos[i]} />
              )}
            </React.Fragment>
          ))}
        </svg>
      </div>
    </div>
  );
};
DocumentTermMatrix.propTypes = {
  clusters: PropTypes.arrayOf(
    PropTypes.shape({
      members: PropTypes.array.isRequired,
      expanded: PropTypes.bool.isRequired,
      keywords: PropTypes.arrayOf(PropTypes.string).isRequired,
      labels: PropTypes.string
    })
  ),
  highlightSnippet: PropTypes.func
};

export default DocumentTermMatrix;
